import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Input, Modal, Table } from "antd";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { searchIcon } from "./../../svgs";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "store";
import { parseTotalCountWithServiceStatus, parseVehiclesTable } from "utils/parsers";
import { initializeVehicles, onChangeVehiclesFilters, setVehiclesSearch } from "store/actions";
import { TableProps } from "antd/lib/table";
import { renderAssignees, serviceStatusCell } from "components/tab-content/vehicles-content/cell-renders";
import useDebounce from "utils/hooks";

ChartJS.register(ArcElement, Tooltip, Legend);

type CategoryData = {
  total_vehicle_with_service_status: { total_vehicles: number; vehicle_service_status: string }[];
};

// ─── Status Colors ───
const STATUS_COLORS: { [key: string]: { background: string; border: string } } = {
  'Operational': {
    background: 'rgba(10, 189, 105, 1)',
    border: 'rgba(200, 200, 200, 0.15)',
  },
  'OPERATIONAL': {
    background: 'rgba(10, 189, 105, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'In Maintenance': {
    background: 'rgba(255, 165, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'IN_MAINTENANCE': {
    background: 'rgba(255, 165, 0, 1)',
     border: 'rgba(200, 200, 200, 0.15)',
  },
  'Marked Broken': {
    background: 'rgba(243, 8, 8, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'MARKED_BROKEN': {
    background: 'rgba(243, 8, 8, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'Retired': {
    background: 'rgba(128, 0, 128, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'RETIRED': {
    background: 'rgba(128, 0, 128, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
};

const DEFAULT_COLOR = {
  background: 'rgba(128, 128, 128, 1)',
  border: 'rgba(128, 128, 128, 0.3)',
};

const getStatusColor = (status: string) => {
  return STATUS_COLORS[status] || DEFAULT_COLOR;
};

export default function VehiclesRenderChart() {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [isFilterFromPieClick, setIsFilterFromPieClick] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const chartRef = useRef(null);

  const dispatch = useDispatch();

  const {
    vehicles: {
      vehicles,
      search,
      vehicleCountWithStatus,
      filters: { updated },
    },
  } = useSelector<AppState, AppState>((state) => state);
  const { userType } = useSelector<AppState, AppState["common"]>(
    (state) => state.common
  );
  const data = useMemo(() => parseVehiclesTable(vehicles), [vehicles]);

  const categoryData: CategoryData = useMemo(() => {
    const parsedData = parseTotalCountWithServiceStatus(vehicleCountWithStatus);
    return {
      total_vehicle_with_service_status: Array.isArray(parsedData)
        ? parsedData
        : [],
    };
  }, [vehicleCountWithStatus]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isExtraSmall = windowWidth < 300;
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  const pieChartData = useMemo(() => {
    const statuses = categoryData.total_vehicle_with_service_status.map(
      (vehicle) => vehicle.vehicle_service_status
    );
    const backgroundColors = statuses.map((s) => getStatusColor(s).background);
    const borderColors = statuses.map((s) => getStatusColor(s).border);

    return {
      labels: statuses,
      datasets: [
        {
          data: categoryData.total_vehicle_with_service_status.map((v) =>
            Number(v.total_vehicles)
          ),
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 2,
          hoverOffset: isMobile ? 5 : 10,
        },
      ],
    };
  }, [categoryData, isMobile]);

  const totalVehicles = categoryData.total_vehicle_with_service_status.reduce(
    (acc, curr) => acc + curr.total_vehicles,
    0
  );

  // ─── Chart options — legend HIDDEN ───
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "55%",
      layout: {
        padding: isExtraSmall ? 1 : isMobile ? 3 : 5,
      },
      plugins: {
        legend: {
          display: false, // ✅ Legend hidden
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || "";
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce(
                (a: number, b: number) => a + b,
                0
              );
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} vehicles (${percentage}%)`;
            },
          },
          bodyFont: {
            size: isExtraSmall ? 9 : isMobile ? 10 : 12,
          },
        },
      },
      onClick: (event: any, elements: any) => {
        if (userType !== 2 && elements.length > 0) {
          const elementIndex = elements[0].index;
          const category =
            categoryData.total_vehicle_with_service_status[elementIndex]
              .vehicle_service_status;
          handlePieClick(category);
        }
      },
    }),
    [isExtraSmall, isMobile, userType, categoryData]
  );

  const handlePieClick = (category: string) => {
    setSelectedCategory(category);
    setShouldOpenModal(true);
    setIsFilterFromPieClick(true);
    dispatch(setVehiclesSearch(""));

    if (category === "In Maintenance") {
      dispatch(onChangeVehiclesFilters("Service Status", ["IN_MAINTENANCE"], "list"));
    } else if (category === "Marked Broken") {
      dispatch(onChangeVehiclesFilters("Service Status", ["MARKED_BROKEN"], "list"));
    } else {
      dispatch(onChangeVehiclesFilters("Service Status", [category.toUpperCase()], "list"));
    }
  };

  // ─── Stats card click → same as pie click ───
  const handleStatClick = (status: string) => {
    if (userType !== 2) {
      handlePieClick(status);
    }
  };

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setVehiclesSearch(event.target.value));
    },
    [dispatch]
  );

  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    if (debounceSearchString && !isFilterFromPieClick) {
      dispatch(setVehiclesSearch(debounceSearchString));
    }
    if (isFilterFromPieClick) {
      setIsFilterFromPieClick(false);
    }
  }, [debounceSearchString, dispatch, isFilterFromPieClick]);

  useEffect(() => {
    if (updated && shouldOpenModal) {
      dispatch(initializeVehicles(1, updated));
      setIsModalVisible(true);
      setShouldOpenModal(false);
    }
  }, [updated, shouldOpenModal, dispatch]);

  const filteredCategoryData = useMemo(() => {
    if (!selectedCategory) return [];
    return categoryData.total_vehicle_with_service_status.filter(
      (vehicle) => vehicle.vehicle_service_status === selectedCategory
    );
  }, [selectedCategory, categoryData]);

  const columns: TableProps<any>["columns"] = [
    {
      title: "Vehicle ID",
      dataIndex: "vehicle_id",
      width: isMobile ? 80 : 100,
      ellipsis: true,
      fixed: isMobile ? "left" : undefined,
    },
    {
      title: "Billing",
      dataIndex: "is_billing_active",
      width: isMobile ? 80 : 130,
      ellipsis: true,
      responsive: ["md"] as any,
    },
    {
      title: "Model",
      dataIndex: "model",
      width: isMobile ? 100 : 130,
      ellipsis: true,
    },
    {
      title: "Assignee",
      dataIndex: "driver_name",
      width: isMobile ? 80 : 100,
      ellipsis: true,
      render: renderAssignees,
      responsive: ["sm"] as any,
    },
    {
      title: "Device ID",
      dataIndex: "iot_id",
      width: 130,
      ellipsis: true,
      responsive: ["lg"] as any,
    },
    {
      title: "Service Status",
      dataIndex: "vehicle_service_status",
      width: isMobile ? 100 : 140,
      ellipsis: true,
      render: serviceStatusCell,
    },
    {
      title: "Zone",
      dataIndex: "zone",
      width: 140,
      ellipsis: true,
      responsive: ["md"] as any,
    },
    {
      title: "Checked Out",
      dataIndex: "checkout_by_name",
      width: 130,
      ellipsis: true,
      responsive: ["lg"] as any,
    },
    {
      title: "VIN",
      dataIndex: "vin",
      width: 130,
      ellipsis: true,
      align: "center",
      responsive: ["lg"] as any,
    },
  ];

  const getChartHeight = () => {
    if (isExtraSmall) return "200px";
    if (isMobile) return "250px";
    if (isTablet) return "280px";
    return "150px";
  };

  const getModalWidth = () => {
    if (isMobile) return "95%";
    if (isTablet) return "90%";
    return 800;
  };

  return (
    <div className="vehicles-chart-container">
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ margin: 0, fontSize: isMobile ? "14px" : "16px" }}>
          Vehicles Status
        </h3>
        {totalVehicles > 0 && (
          <span
            style={{
              fontSize: isMobile ? 11 : 13,
              color: "#888",
              background: "#f5f5f5",
              padding: "2px 8px",
              borderRadius: "10px",
            }}
          >
            Total: {totalVehicles}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="pie-chart-wrapper">
        {categoryData.total_vehicle_with_service_status.length > 0 ? (
          <div
            className="pie-chart-container"
            style={{
              height: getChartHeight(),
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              padding: isMobile ? "10px" : "0",
            }}
          >
            <Doughnut
              ref={chartRef}
              data={pieChartData}
              options={chartOptions}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
              textAlign: "center",
            }}
          >
            <p>No records available to display.</p>
          </div>
        )}
      </div>

      {/* ✅ Stats Cards */}
      {categoryData.total_vehicle_with_service_status.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, 1fr)"
              : `repeat(${Math.min(categoryData.total_vehicle_with_service_status.length, 5)}, minmax(0, 1fr))`,
            gap: isMobile ? "6px" : "8px",
            marginTop: "15px",
          }}
        >
          {categoryData.total_vehicle_with_service_status.map((item) => {
            const color = getStatusColor(item.vehicle_service_status);
            return (
              <div
                key={item.vehicle_service_status}
                onClick={() => handleStatClick(item.vehicle_service_status)}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "6px 4px" : "8px",
                  borderRadius: "8px",
                  background: color.border,
                  cursor: userType !== 2 ? "pointer" : "default",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  border: `1px solid ${color.background}20`,
                }}
                onMouseEnter={(e) => {
                  if (userType !== 2) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = `0 2px 8px ${color.background}40`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "16px" : "20px",
                    fontWeight: 700,
                    color: color.background,
                  }}
                >
                  {item.total_vehicles}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "9px" : "11px",
                    color: "#555",
                    marginTop: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.vehicle_service_status}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategory(null);
          setShouldOpenModal(false);
          setIsFilterFromPieClick(false);
          dispatch(setVehiclesSearch(""));
          dispatch(onChangeVehiclesFilters("Service Status", [], "list"));
        }}
        footer={null}
        width={getModalWidth()}
        className="vehicles-modal"
        bodyStyle={{
          padding: isMobile ? "12px" : "24px",
          maxHeight: isMobile ? "70vh" : "80vh",
          overflow: "auto",
        }}
      >
        <Input
          className="--standard vehicles-search-input"
          placeholder="Search vehicle..."
          prefix={searchIcon}
          value={search}
          onChange={onChange}
          style={{ marginBottom: isMobile ? "12px" : "16px" }}
        />
        <div className="vehicles-table-container">
          {filteredCategoryData.length > 0 ? (
            <Table
              dataSource={data.filter(
                (vehicle) =>
                  (!selectedCategory ||
                    vehicle.vehicle_service_status === selectedCategory) &&
                  vehicle.vehicle_id
                    .toLowerCase()
                    .includes(debounceSearchString.toLowerCase())
              )}
              columns={columns}
              pagination={isMobile ? { pageSize: 5, size: "small" } : false}
              scroll={{
                y: isMobile ? 250 : 300,
                x: isMobile ? "max-content" : undefined,
              }}
              size={isMobile ? "small" : "middle"}
            />
          ) : (
            <p>No data available for this category.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}